/**
 * CSV Utility for Mirinae Platform
 * Handles UTF-8 BOM for Excel compatibility
 */

export const CSVUtils = {
    /**
     * Converts an array of objects to CSV string
     * @param {Array<Object>} data - Array of objects
     * @param {Array<String>} columns - List of header columns (keys)
     */
    toCSV: (data, columns) => {
        if (!data || !data.length) return '';

        const headers = columns.join(',');
        const rows = data.map(row => {
            return columns.map(col => {
                let val = row[col] === undefined || row[col] === null ? '' : row[col];
                // Escape quotes and wrap in quotes if contains comma
                val = String(val).replace(/"/g, '""');
                if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                    val = `"${val}"`;
                }
                return val;
            }).join(',');
        });

        return [headers, ...rows].join('\n');
    },

    /**
     * Triggers a browser download for CSV
     * @param {String} csvContent 
     * @param {String} fileName 
     */
    download: (csvContent, fileName) => {
        // Add BOM for Excel UTF-8 compatibility
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    },

    /**
     * Parses a CSV file into an array of objects
     * @param {File} file 
     * @returns {Promise<Array<Object>>}
     */
    parse: (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const lines = text.split(/\r\n|\n/).filter(line => line.trim() !== '');
                if (lines.length < 2) return resolve([]);

                const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));

                const result = lines.slice(1).map(line => {
                    // Simple split by comma, but respecting quotes is harder without regex/library.
                    // For this specific internal use case (Admin Batch), we assume simple CSV or use a regex splitter.
                    // Robust regex for CSV splitting:
                    const regex = /(?:,|\n|^)("(?:(?:"")*|[^"]*)*"|[^",\n]*|(?:\n|$))/g;
                    const matches = [];
                    let match;
                    while ((match = regex.exec(line)) !== null) {
                        // Fix empty matches at end
                        if (match.index === regex.lastIndex) regex.lastIndex++;
                        if (match[1] !== undefined) matches.push(match[1].replace(/^"|"$/g, '').replace(/""/g, '"'));
                    }
                    // The regex approach might be buggy for complex cases in Vanilla JS without thorough testing.
                    // Falling back to simple split for MVP if fields don't contain commas.
                    // But we expect titles to have commas.

                    // Let's use a simpler heuristic or just basic split if we assume admin uses Excel save.
                    // Actually, let's use a standard simple parser function.
                    return CSVUtils.parseLine(line, headers);
                }).filter(r => r !== null);

                resolve(result);
            };
            reader.onerror = (err) => reject(err);
            reader.readAsText(file);
        });
    },

    parseLine: (line, headers) => {
        const row = {};
        let current = '';
        let inQuote = false;
        let colIndex = 0;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                inQuote = !inQuote;
            } else if (char === ',' && !inQuote) {
                if (colIndex < headers.length) row[headers[colIndex]] = current.trim();
                current = '';
                colIndex++;
            } else {
                current += char;
            }
        }
        if (colIndex < headers.length) row[headers[colIndex]] = current.trim();
        return row;
    }
};
